import React from 'react';
import { Navigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { isAuthenticated } from '../services/auth';

const VALIDATION_STORAGE_KEY = 'auth_validated_at';
const VALIDATION_INTERVAL_MS = 5 * 60 * 1000;

const readLastValidationTime = () => {
  if (typeof window === 'undefined') return 0;
  try {
    return Number(window.sessionStorage.getItem(VALIDATION_STORAGE_KEY) || 0);
  } catch {
    return 0;
  }
};

const writeLastValidationTime = (timestamp) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(VALIDATION_STORAGE_KEY, String(timestamp || 0));
  } catch {
    // Ignore storage errors.
  }
};

export const ProtectedRoute = ({ children }) => {
  const [isChecking, setIsChecking] = React.useState(true);

  const validateSession = React.useCallback(async (force = false) => {
    if (!isAuthenticated()) {
      setIsChecking(false);
      return false;
    }

    const lastValidatedAt = readLastValidationTime();
    if (!force && Date.now() - lastValidatedAt < VALIDATION_INTERVAL_MS) {
      setIsChecking(false);
      return true;
    }

    const isValid = await authAPI.refreshToken();
    if (isValid) {
      writeLastValidationTime(Date.now());
    } else {
      writeLastValidationTime(0);
    }
    setIsChecking(false);
    return isValid;
  }, []);

  React.useEffect(() => {
    let active = true;

    const runInitialValidation = async () => {
      if (!active) return;
      await validateSession(false);
    };

    const handleFocusValidation = async () => {
      if (!active || !isAuthenticated()) return;
      await validateSession(false);
    };

    const handleVisibilityChange = async () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      await handleFocusValidation();
    };

    runInitialValidation();
    window.addEventListener('focus', handleFocusValidation);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      active = false;
      window.removeEventListener('focus', handleFocusValidation);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [validateSession]);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500 dark:border-slate-700 dark:border-t-primary-400" />
          <p className="text-sm text-primary-700 dark:text-primary-300">Checking session...</p>
        </div>
      </div>
    );
  }

  return children;
};
